import React, { useEffect, useMemo, useState } from "react";
import TitleButtons from "../../../../../components/TitleButtons";
import TextField from "../../../../../components/TextField";
import Button from "../../../../../components/Button";

const API_POST =
  "https://api.eva-googlemarine.com/departamentos/coordenacao/embarcacoes";
const API_EMPRESAS =
  "https://api.eva-googlemarine.com/geral/empresa-clientes-nome";

/** --- Notice padrão (igual aos seus) --- */
const NoticeBanner: React.FC<{
  notice: { type: "success" | "error" | "notify"; text: string };
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = true }) => {
  let bg, color, border;
  switch (notice.type) {
    case "success":
      bg = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;
    case "error":
      bg = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;
    default:
      bg = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const RegistrarEmbarcacao: React.FC = () => {
  // Campos
  const [nome, setNome] = useState("");
  const [empresa, setEmpresa] = useState("");

  // Empresas (dropdown)
  const [empresas, setEmpresas] = useState<string[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [empresasErro, setEmpresasErro] = useState<string | null>(null);

  // Envio
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  const canSubmit = useMemo(
    () => nome.trim().length > 0 && empresa.trim().length > 0,
    [nome, empresa]
  );

  function clearAll() {
    setNome("");
    setEmpresa("");
  }

  /** Carrega nomes de empresas */
  const loadEmpresas = async () => {
    setLoadingEmpresas(true);
    setEmpresasErro(null);
    try {
      const res = await fetch(API_EMPRESAS, {
        method: "GET",
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      const json = await res.json();
      if (!res.ok || !json?.ok || !Array.isArray(json?.data)) {
        throw new Error(
          json?.erro || json?.message || "Falha ao carregar empresas."
        );
      }
      // normaliza: remove duplicados e ordena ASC
      const uniq: string[] = Array.from(
        new Set<string>(
          json.data
            .map((n: any) => (n == null ? "" : String(n).trim()))
            .filter((n: string) => n.length > 0)
        )
      ).sort((a, b) => a.localeCompare(b, "pt-BR"));

      setEmpresas(uniq);
    } catch (e: any) {
      setEmpresasErro(e?.message || "Erro ao carregar empresas.");
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  async function handleRegister() {
    setNotice(null);
    const erros: string[] = [];
    if (!nome.trim()) erros.push("Informe o Nome da Embarcação.");
    if (!empresa.trim()) erros.push("Selecione a Empresa.");
    if (erros.length) {
      setNotice({ type: "error", text: erros.join(" ") });
      return;
    }

    const payload: any = {
      nome_embarcacao: nome.trim(),
      empresa_nome: empresa, // troque por empresa_id quando integrar de fato
    };

    setSubmitting(true);
    try {
      const res = await fetch(API_POST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        const msg =
          data?.erro ||
          (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
          data?.message ||
          "Falha ao registrar embarcação.";
        setNotice({ type: "error", text: msg });
        return;
      }
      setNotice({
        type: "success",
        text: `Embarcação registrada! ID ${data?.data?.id ?? ""}.`,
      });
      clearAll();
    } catch (err: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${err?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%" }}>
      <TitleButtons fullBleedLine title="Coordenação - Registrar Embarcação" />

      {(notice?.type === "success" || notice?.type === "notify") && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}

      {empresasErro && (
        <NoticeBanner
          notice={{
            type: "error",
            text:
              empresasErro +
              " Utilize o botão 'Tentar novamente (empresas)' acima.",
          }}
          onClose={() => setEmpresasErro(null)}
          sticky={false}
        />
      )}

      <div style={{ display: "grid", gap: 10, width: "100%" }}>
        {/* Nome da embarcação */}
        <div style={{ width: "100%" }}>
          <TextField
            label="Nome da Embarcação *"
            value={nome}
            onChange={setNome}
            placeholder="Ex.: STARLING TIDE"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        {/* Empresa (dropdown carregado da API) */}
        <div style={{ width: "100%" }}>
          <TextField
            label="Empresa *"
            value={empresa}
            onChange={setEmpresa}
            dropdown
            options={empresas}
            placeholder={loadingEmpresas ? "Carregando..." : "Selecione..."}
            style={{ width: "100%" }}
            disabled={loadingEmpresas || !!empresasErro}
          />
        </div>
      </div>

      {notice?.type === "error" && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={false}
        />
      )}

      <div style={{ marginTop: 12 }}>
        <Button
          text={submitting ? "Registrando..." : "Registrar Embarcação"}
          onClick={handleRegister}
          disabled={
            submitting || !canSubmit || loadingEmpresas || !!empresasErro
          }
        />
      </div>
    </div>
  );
};

export default RegistrarEmbarcacao;
